import React, { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { format, parseISO } from "date-fns";
import { Pencil } from "lucide-react";
import { Calendar } from "../../ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "../../ui/popover";
import { handleGetLaunchDateWindow } from "../../../utils/helperFunctions";
import { GET_AD_REQUESTS_QUERY_KEY } from "../../../utils/constants/queryKeys";
import {
  LAUNCH_DATE_LOCK_TITLES,
  handleIsRequestLaunched,
} from "../../../utils/constants/AdRequest";
import toast from "react-hot-toast";
import AdsAPIs from "../../../apiRoutes/Ads";
import useAxiosPrivate from "../../../hooks/useAxiosPrivate";
import CustomModal from "../../common/CustomModal";

/** Launch date with an inline picker; editable until 11 PM IST on the launch day, or later if it never launched. */
const LaunchDateCell = ({ request, canEdit }) => {
  const [pendingDate, setPendingDate] = useState(null);
  const [open, setOpen] = useState(false);
  const axiosPrivate = useAxiosPrivate();
  const queryClient = useQueryClient();
  const { mutate, isPending: isLoading } = useMutation({
    mutationFn: ({ launchDate }) =>
      AdsAPIs.handleUpdateLaunchDate({
        axiosPrivate,
        id: request.id,
        launch_date: launchDate,
      }),
    onSuccess: () => {
      toast.success("Launch date updated");
      setPendingDate(null);
      queryClient.invalidateQueries({ queryKey: GET_AD_REQUESTS_QUERY_KEY });
    },
  });
  const launchDate = request.launch_date;

  if (!launchDate) return <span className="text-gray-400">—</span>;

  const { editable, earliest, lockReason } = handleGetLaunchDateWindow({
    launchDate,
    launched: handleIsRequestLaunched({ summary: request.launch_summary }),
  });
  const label = format(parseISO(launchDate), "d MMM ''yy");

  if (!canEdit || !editable) {
    return (
      <span
        className="text-gray-700"
        title={canEdit ? LAUNCH_DATE_LOCK_TITLES[lockReason] : undefined}
      >
        {label}
      </span>
    );
  }

  const handleSelect = (day) => {
    setOpen(false);
    const picked = day && format(day, "yyyy-MM-dd");
    if (picked && picked !== launchDate) setPendingDate(picked);
  };

  const handleConfirm = () => {
    if (pendingDate) mutate({ launchDate: pendingDate });
  };

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={isLoading}
          className="-mx-2 inline-flex items-center gap-1.5 rounded-md px-2 py-1 text-gray-700 hover:bg-gray-100 disabled:opacity-60"
          title="Change launch date"
        >
          {label}
          <Pencil className="h-3 w-3 text-gray-400" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        className="w-auto p-0"
        align="start"
        onClick={(e) => e.stopPropagation()}
      >
        <Calendar
          mode="single"
          selected={parseISO(launchDate)}
          defaultMonth={parseISO(launchDate)}
          disabled={{ before: parseISO(earliest) }}
          onSelect={handleSelect}
        />
      </PopoverContent>
      <CustomModal
        open={!!pendingDate}
        title="Change launch date?"
        isLoading={isLoading}
        onClose={() => !isLoading && setPendingDate(null)}
        handleConfirmation={handleConfirm}
        handleConfirmationBtnText="Change date"
      >
        {pendingDate && (
          <div className="space-y-2 whitespace-normal py-2 text-sm text-gray-700">
            <p
              className="truncate font-medium text-gray-900"
              title={request.adset_name}
            >
              {request.adset_name || `Request #${request.id}`}
            </p>
            <p>
              Launch date moves from{" "}
              <span className="font-medium">{label}</span> to{" "}
              <span className="font-medium">
                {format(parseISO(pendingDate), "d MMM ''yy")}
              </span>
              .
            </p>
            <p className="text-xs text-gray-500">
              The change is logged with your name and the time.
            </p>
          </div>
        )}
      </CustomModal>
    </Popover>
  );
};

export default LaunchDateCell;
